const DEFAULT_HASH_LENGTH = 30;
const FNV_64_PRIME = 0x100000001b3n;
const UINT_64_MASK = (1n << 64n) - 1n;
const FNV_SEEDS = [0xcbf29ce484222325n, 0x84222325cbf29ce4n, 0x9e3779b97f4a7c15n];
// no # due to reservation for view/index separator
// no ` due to use as the delimiter in formulas
export const HASHED_NAME_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_';
export function stableHash(payload, options = {}) {
    return encodeHash(stableStringify(payload), options);
}
export function generateHashedName(payload, options = {}) {
    const { syntaxVariations, commitId: commit, ...hashOptions } = options;
    return stableHash({ commit, payload, syntaxVariations }, hashOptions);
}
export function stableStringify(value) {
    return stringifyStableValue(value, new WeakSet());
}
function encodeHash(input, options) {
    const length = options.length ?? DEFAULT_HASH_LENGTH;
    const alphabet = options.alphabet ?? HASHED_NAME_ALPHABET;
    if (!Number.isInteger(length) || length <= 0) {
        throw new Error('Stable hash length must be a positive integer');
    }
    if (new Set(alphabet).size !== alphabet.length || alphabet.length < 2) {
        throw new Error('Stable hash alphabet must contain at least two unique characters');
    }
    const combined = FNV_SEEDS.reduce((acc, seed) => (acc << 64n) | fnv1a64(input, seed), 0n);
    return encodePositiveBigInt(combined, alphabet).padStart(length, alphabet[0]).slice(-length);
}
function fnv1a64(input, seed) {
    let hash = seed;
    for (const byte of new TextEncoder().encode(input)) {
        hash ^= BigInt(byte);
        hash = (hash * FNV_64_PRIME) & UINT_64_MASK;
    }
    return hash;
}
function encodePositiveBigInt(value, alphabet) {
    if (value === 0n)
        return alphabet[0];
    const base = BigInt(alphabet.length);
    let remaining = value;
    let output = '';
    while (remaining > 0n) {
        const index = Number(remaining % base);
        output = alphabet[index] + output;
        remaining /= base;
    }
    return output;
}
function stringifyStableValue(value, seen) {
    if (value === null)
        return 'null';
    switch (typeof value) {
        case 'bigint':
            return `{"$bigint":${JSON.stringify(value.toString())}}`;
        case 'boolean':
            return value ? 'true' : 'false';
        case 'function':
            throw new TypeError('Cannot stable-hash function values');
        case 'number':
            return Number.isFinite(value) ? String(value) : `{"$number":${JSON.stringify(String(value))}}`;
        case 'string':
            return JSON.stringify(value);
        case 'symbol':
            throw new TypeError('Cannot stable-hash symbol values');
        case 'undefined':
            return '{"$undefined":true}';
        case 'object':
            return stringifyStableObject(value, seen);
    }
    throw new TypeError(`Cannot stable-hash value of type ${typeof value}`);
}
function stringifyStableObject(value, seen) {
    if (seen.has(value)) {
        throw new TypeError('Cannot stable-hash circular values');
    }
    seen.add(value);
    try {
        if (Array.isArray(value)) {
            const values = value.map((item) => stringifyStableValue(item, seen));
            return `[${values.join(',')}]`;
        }
        if (value instanceof Date) {
            return `{"$date":${JSON.stringify(value.toISOString())}}`;
        }
        if (value instanceof Map) {
            const entries = Array.from(value.entries())
                .map(([key, entryValue]) => [stringifyStableValue(key, seen), stringifyStableValue(entryValue, seen)])
                .sort(([left], [right]) => compareStableStrings(left, right));
            return `{"$map":[${entries.map(([key, entryValue]) => `[${key},${entryValue}]`).join(',')}]}`;
        }
        if (value instanceof Set) {
            const values = Array.from(value.values())
                .map((item) => stringifyStableValue(item, seen))
                .sort(compareStableStrings);
            return `{"$set":[${values.join(',')}]}`;
        }
        const record = value;
        return `{${Object.keys(record)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${stringifyStableValue(record[key], seen)}`)
            .join(',')}}`;
    }
    finally {
        seen.delete(value);
    }
}
function compareStableStrings(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
